import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core'
import { CoreService, HsApiService, NotificationService, ProductMdClassService } from '../../../../services'
import { SchemaUtil, roundEnovaDown, getThumbUrl as imageGetThumbUrl } from '../../../../utils'
import { TranslateService } from '@ngx-translate/core'
import { Product, File, Currency } from 'depoto-core/src/entities'
import { debounce } from 'lodash'
import { PurchaseOrderItem } from '../../../product-purchase-order-creation/components/modal-product-search-purchase/modal-product-search-purchase.component'
import { ModalContainerComponent } from '../../../../components/_common/modal-container/modal-container.component'
import { ProductDetailService } from '../../../../services/product-detail.service'
import { ProductCreatingService } from '../../../../services/product-creating.service'

@Component({
  selector: 'modal-product-search-purchase-hs',
  templateUrl: 'modal-product-search-purchase-hs.component.html',
  styleUrls: ['modal-product-search-purchase-hs.component.scss'],
})
export class ModalProductSearchPurchaseHsComponent implements OnInit {
  @ViewChild('modalHsSyncInOrder') public childModal: ModalContainerComponent
  @ViewChild('searchInputInModal') public searchInputInModal: any
  @Input() hsProductRef: string
  @Output() onInitialFetching: EventEmitter<boolean> = new EventEmitter<boolean>()
  @Output() onSelected: EventEmitter<any> = new EventEmitter<any>()
  @Output() public chosenProduct: EventEmitter<PurchaseOrderItem> = new EventEmitter()
  isLoading = false
  isNotFound = false
  allowBackdropClick = false
  timer = null
  searchPhrase: string = ''
  searchPhraseProducts: string = ''
  templates = []
  products = []
  paginationDetail = ''
  searchTab: 'hs' | 'own' = 'own'
  modalContent: 'search-tabs' | 'connect-confirm' | 'connect-choose' = 'search-tabs'
  productToConnectWithHsTemplate: Product
  customerCode: string
  purchaseCurrencyFromCompany: Currency // there is no way to change this across hs, so it's certain we can use this

  constructor(
    private core: CoreService,
    private hsApi: HsApiService,
    public productDetailService: ProductDetailService,
    public productCreatingService: ProductCreatingService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    public productMdClassService: ProductMdClassService,
  ) {}

  async ngOnInit() {
    this.isLoading = false
    this.customerCode = this.core.services.user.user?.company?.externalId
    this.purchaseCurrencyFromCompany = this.core.services.user.user?.company?.currency
  }

  showModalAndFocusOnSearchInput() {
    if (this.products?.length) {
      this.products = []
    }
    this.modalContent = 'search-tabs'
    if (!this.childModal.isShown()) {
      this.childModal.showChildModal()
      this.allowBackdropClick = false
      setTimeout(() => {
        this.searchInputInModal?.formRow?.nativeElement?.getElementsByTagName('input')?.[0]?.focus()
        this.searchForProduct()
      }, 500) // modal show animation timeout..
      setTimeout(() => {
        this.allowBackdropClick = true
      }, 2500) // disable backdrop click for first 2500ms
    }
  }

  updateSearchValue(val: string) {
    this.searchPhrase = val
    if (val?.length > 0) {
      this.searchForProduct()
    } else {
      this.childModal.hideChildModal()
    }
  }

  async fetchTemplates() {
    this.templates = []
    const loadingTimer = setTimeout(() => {
      this.isLoading = true
    }, 500)
    this.isNotFound = false
    try {
      const response = await this.hsApi.searchForHsTemplate(this.searchPhrase, [], true)
      clearTimeout(loadingTimer)
      this.isLoading = false
      this.paginationDetail = this.translateService.instant('product.template.paginationDetail', {
        numberShown: `${response?.items?.length || 0}`,
        numberAll: `${response?.paginator?.totalCount || 0}`,
      })
      this.templates = response?.items?.map(t => ({ ...t, isLoadingDetails: true })) || []

      if (!this.templates.length) {
        this.isNotFound = true
      }

      this.templates.forEach(async t => {
        try {
          await this.getEvidentistQuantityAndDiscountsForTemplate(t)
        } catch (err) {
          console.log(err)
        } finally {
          t.isLoadingDetails = false
        }
      })
    } catch (err) {
      console.log(err)
      clearTimeout(loadingTimer)
      this.isLoading = false
    }
  }

  async getEvidentistQuantityAndDiscountsForTemplate(template: any) {
    let evidentistProducts = await this.core.services.product.getList(
      { filters: { externalId: template.hsId } },
      SchemaUtil.productPurchaseOrder,
    )

    if (!evidentistProducts?.items?.length && template.mCode) {
      evidentistProducts = await this.core.services.product.getList(
        { filters: { code: template.mCode } },
        SchemaUtil.productPurchaseOrder,
      )
    }

    if (evidentistProducts?.items?.length) {
      template.existingProduct = evidentistProducts.items[0]
      template.quantityEvidentist = evidentistProducts?.items[0].quantityAvailable
    } else {
      template.quantityEvidentist = 0
    }
    const productDiscounts = await this.hsApi.getHsDiscounts(template.hsId)
    if (!productDiscounts || productDiscounts?.error || !productDiscounts?.bestDiscount?.sellable) {
      return template
    }
    template.discounts = productDiscounts
    template.discount = productDiscounts.bestDiscount?.discountPercent || 0
    const vatMultiplier = 1 + (template.vatRate || 0) / 100
    template.purchasePrice = productDiscounts.bestDiscount?.purchasePrice
    template.purchasePriceWithVat = template.purchasePrice * vatMultiplier
    template.purchasePriceAll = template.purchasePrice * (template.quantity || 1)
    template.purchasePriceAllWithVat = template.purchasePriceAll * vatMultiplier
    if (template.discount > 0) {
      template.purchasePriceWithDiscount = roundEnovaDown(template.purchasePrice * (1 - template.discount / 100))
      template.purchasePriceWithVatAndDiscount = template.purchasePriceWithDiscount * vatMultiplier
      template.purchasePriceAllWithDiscount = template.purchasePriceWithDiscount * (template.quantity || 1)
      template.purchasePriceAllWithVatAndDiscount = template.purchasePriceAllWithDiscount * vatMultiplier
    } else {
      template.purchasePriceWithDiscount = template.purchasePrice
      template.purchasePriceWithVatAndDiscount = template.purchasePriceWithVat
      template.purchasePriceAllWithDiscount = template.purchasePriceAll
      template.purchasePriceAllWithVatAndDiscount = template.purchasePriceAllWithVat
    }
    return template
  }

  debounce(func, timeout = 300) {
    return (...args) => {
      clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        func.apply(this, args)
      }, timeout)
    }
  }
  public async productSearchChange() {
    this.debounce(() => this.searchForProduct(), 500)()
  }

  public async templateSearchChange() {
    this.debounce(() => this.fetchTemplates(), 500)()
  }

  async updateExistingProductFromTemplate(product, template) {
    const p: any = { id: product.id }
    let changed = false

    if (!product.code && template.mCode) {
      p.code = template.mCode
      changed = true
    }
    if (!product.code2 && template.dlCode) {
      p.code2 = template.dlCode
      changed = true
    }
    if (!product.code3 && template.manuCode) {
      p.code3 = template.manuCode
      changed = true
    }
    if (!product.ean && template.ean) {
      p.ean = template.ean
      changed = true
    }
    if (!product.externalId && template.hsId) {
      p.externalId = template.hsId
      changed = true
    }
    if (!product.name && template.name) {
      p.name = template.name
      changed = true
    }
    if (!product.vat?.id && template.vatRate !== undefined) {
      const vats = this.core.lists.vats
      const vat = vats.find(v => v.percent === template.vatRate)
      if (vat) {
        p.vat = { id: vat.id }
        changed = true
      }
    }
    const hsSupplierId = this.core.lists.suppliers.find(s => s.externalId === 'henryschein')?.id
    if (!product.supplier?.id || product?.supplier?.id !== hsSupplierId) {
      p.supplier = { id: hsSupplierId }
      changed = true
    }
    if (template.mdClass) {
      const currentMdClass = this.productMdClassService.getCurrentValue(product)
      if (!currentMdClass) {
        await this.productMdClassService.save(product, template.mdClass)
      }
    }
    if (changed) {
      await this.core.services.product.updateProductPart(p, SchemaUtil.basic)
    }
  }

  async selectTemplate(template, isOnInit?: boolean) {
    if (template.isLoadingDetails) {
      return
    }

    if (template.existingProduct) {
      await this.updateExistingProductFromTemplate(template.existingProduct, template)
      const prod = new Product(template.existingProduct)
      // HS specific info from template
      // prod.discount = template.discount
      // prod.discounts = template.discounts
      prod.purchasePrice = template.purchasePrice
      this.chooseProduct(prod)
      return
    }

    let parentToCreate
    this.productDetailService.isProductFromTemplate = true
    this.productCreatingService.clearOptions()
    if (template?.parent?.mCode) {
      parentToCreate = template.parent
    }
    const mainImageToCreate = this.productCreatingService.getFirstImage(template)
    if (template.children?.length > 0) {
      template.children = await this.productCreatingService.checkIfChildrenExist(
        template.children,
        this.productDetailService.vats,
        this.productDetailService.suppliers,
      )
    }
    await this.convertTemplateToProduct(template)
    if (this.core.services.user.user?.company?.batchExpirationMode === 'enabled-everywhere') {
      this.productDetailService.product.enabledBatchExpiration = true
      // setTimeout(() => {
      //   this.productDetailService.product.enabledBatchExpiration = true
      // }, 500) // after navigation hack..
    }
    await this.createProductFromTemplate(template, parentToCreate, mainImageToCreate)
  }

  switchTab(tab: 'own' | 'hs') {
    this.searchTab = tab
    if (tab === 'hs' && !this.templates?.length) {
      this.fetchTemplates()
    } else if (tab === 'own' && !this.products?.length) {
      this.searchForProduct()
    }
  }

  get totalLoadingProgress(): { isTotalLoading: boolean; percentage: number } {
    if (this.modalContent === 'connect-choose' || this.searchTab === 'hs') {
      if (this.isLoading) {
        return { isTotalLoading: true, percentage: 0 }
      }
      const total = this.templates?.length || 0
      if (total === 0) {
        return { isTotalLoading: false, percentage: 100 }
      }
      const loaded = this.templates.filter(t => !t.isLoadingDetails).length
      const percentage = Math.round((loaded / total) * 100)
      return {
        isTotalLoading: loaded < total,
        percentage: percentage,
      }
    } else {
      if (this.isLoading) {
        return { isTotalLoading: true, percentage: 0 }
      }
      const total = this.products?.length || 0
      if (total === 0) {
        return { isTotalLoading: false, percentage: 100 }
      }
      const loaded = this.products.filter(p => !p.isLoadingDetails).length
      const percentage = Math.round((loaded / total) * 100)
      return {
        isTotalLoading: loaded < total,
        percentage: percentage,
      }
    }
  }

  async searchForProduct() {
    if (this.isLoading) {
      return
    }
    this.products = []
    const loadingTimer = setTimeout(() => {
      this.isLoading = true
    }, 500)
    this.isNotFound = false
    try {
      const products = await this.core.services.product
        .getList(
          {
            sort: 'name',
            direction: 'asc',
            filters: {
              fulltext: this.searchPhrase?.trim(),
              listType: 'children_and_parents_without_children',
              // availability: this.selectedAvailability,
              enabled: true,
            },
          },
          SchemaUtil.productPurchaseOrder,
        )
        .then(result => {
          clearTimeout(loadingTimer)
          this.isLoading = false
          if (!result?.items?.length) {
            this.isNotFound = true
          }
          return (
            result?.items?.map(p => {
              const prod = new Product(p)
              prod['isLoadingDetails'] = true
              return prod
            }) || []
          )
        })
      this.products = products
      this.products.forEach(async p => {
        try {
          await this.hsApi.getQuantityHsForProduct(p)
          await this.hsApi.getDiscountsForProduct(p)
        } catch (e) {
          console.log('product not existing when setting quantityHs', e, p)
        } finally {
          p['isLoadingDetails'] = false
        }
      })
    } catch (e) {
      clearTimeout(loadingTimer)
      this.isLoading = false
      console.log(e)
    }
  }

  public async showChildModal() {
    this.childModal.showChildModal()
  }

  public hideChildModal(): void {
    this.childModal.hideChildModal()
  }

  getThumbUrl(image: File, format: 'w135wp' | 'w2048wp' = 'w135wp'): string {
    return imageGetThumbUrl(image, format)
  }

  async connectTemplateToProduct(template) {
    if (template.isLoadingDetails) {
      return
    }
    this.productToConnectWithHsTemplate.externalId = template.hsId
    const p = {
      id: this.productToConnectWithHsTemplate.id,
      externalId: `${this.productToConnectWithHsTemplate.externalId}`,
    }
    const res = await this.core.services.product.updateProductPart(p, SchemaUtil.productPurchaseOrder)
    if (template.mdClass) {
      await this.productMdClassService.save(this.productToConnectWithHsTemplate, template.mdClass)
    }
    if (res?.id) {
      this.chooseProduct(new Product(res))
    }
  }

  async selectProduct(prod: Product | any) {
    if (prod.isLoadingDetails) {
      return
    }
    if (prod.externalId?.length > 0) {
      // await this.hsApi.getDiscountsForProduct(prod) // redundant now
      if (!prod.discounts || prod.discounts?.error || !prod.discounts?.bestDiscount?.sellable) {
        this.notificationService.error(
          prod.discounts?.error?.length
            ? prod.discounts?.error
            : this.translateService.instant('purchaseOrder.error.product.notSellable'),
        )
        this.switchToConnectConfirmMode(prod)
        return
      }
      this.productToConnectWithHsTemplate = undefined
      this.chooseProduct(prod)
    } else {
      this.switchToConnectConfirmMode(prod)
    }
  }

  switchToConnectConfirmMode(prod: Product) {
    this.productToConnectWithHsTemplate = prod
    this.modalContent = 'connect-confirm'
    this.searchPhrase = prod.name
    this.fetchTemplates()
  }

  chooseProduct($event: Product | any) {
    const purchasePriceWithDiscount = roundEnovaDown($event.purchasePrice * (1 - ($event.discount || 0) / 100))
    const purchaseOrderItem: PurchaseOrderItem = {
      product: $event.id,
      purchasePrice: $event.purchasePrice,
      currency: $event.purchaseCurrency,
      quantity: 1,
      note: '',
      mainImage: $event.mainImage,
      ean: $event.ean,
      code: $event.code,
      name: $event.name,
      fullName: $event.fullName,
      vat: $event.vat,
      tags: $event.tags,
      packs: $event.packs,
      quantityAvailable: $event.quantityAvailable,
      quantityHs: $event.quantityHs,
      discount: $event.discount || 0, // active discount in percent
      purchasePriceWithDiscount: purchasePriceWithDiscount,
      purchasePriceWithVatAndDiscount: purchasePriceWithDiscount / ($event.vat?.coefficient || 1),
      purchasePriceAllWithDiscount: purchasePriceWithDiscount * 1,
      purchasePriceAllWithVatAndDiscount: purchasePriceWithDiscount / ($event.vat?.coefficient || 1),
      discounts: $event.discounts || {}, // all discounts for reference
      externalId: $event.externalId,
      customData: $event.customData,
    }
    this.chosenProduct.emit(purchaseOrderItem)
    this.childModal.hideChildModal()
    this.searchPhrase = ''
  }

  isCountByPieces(prod: Product): boolean {
    return prod?.tags?.some(t => t.externalId === 'hs-count-by-piece')
  }

  async convertTemplateToProduct(template) {
    if (template) {
      this.productDetailService.piecesInPack = template.packQuantity
      if (template.packQuantity > 1) {
        this.productDetailService.isCountByPiece = true
      }
      this.productDetailService.product = await this.productCreatingService.getProductValues(
        template,
        false,
        this.core.lists.vats,
      )
      if (template.mdClass) {
        this.productDetailService.selectedMdClass = this.productMdClassService.normalizeMdClass(template.mdClass)
      }
    }
  }

  public async createProductFromTemplate(template: any, parentToCreate?: any, mainImageToCreate?: any): Promise<void> {
    this.productDetailService.submitted = true
    if (parentToCreate) {
      this.productDetailService.product.parent = await this.productCreatingService.createParentIfNotExists(
        parentToCreate,
        this.productDetailService.vats,
        this.productDetailService.suppliers,
      )
    }
    try {
      this.productDetailService.loading = true
      this.productDetailService.product.externalId = `${template.hsId}`
      if (!this.productDetailService.product?.vat?.id) {
        this.notificationService.error(
          this.translateService.instant('alert.vat.error.createProductFromTemplate') + ': ' + template.vatRate,
        )
        this.productDetailService.loading = false
        return
      }
      this.productDetailService.product.enabled = true

      if (this.core.services.user.user?.company?.batchExpirationMode === 'enabled-everywhere') {
        this.productDetailService.product.enabledBatchExpiration = true
      }
      // this.productDetailService.product.sellPrice = 0
      this.notificationService.avoidErrorAlerts = true
      if (
        this.productDetailService.product?.customData &&
        typeof this.productDetailService.product?.customData !== 'string'
      ) {
        this.productDetailService.product.customData = JSON.stringify(this.productDetailService.product.customData)
      }
      this.productDetailService.product.supplier = this.core.lists.suppliers.find(s => s.externalId === 'henryschein')
      let result = await this.core.services.product.create(
        this.productDetailService.product,
        SchemaUtil.productPurchaseOrder,
      )
      // if create failed bcs duplicate EAN, send again without EANs
      if (!result) {
        // console.log({result}) // todo check for duplicate EAN!!
        result = await this.core.services.product.create(
          {
            ...this.productDetailService.product,
            ...{ ean: undefined, ean2: undefined, ean3: undefined, ean4: undefined, ean5: undefined },
          },
          SchemaUtil.productPurchaseOrder,
        )
      }
      this.notificationService.avoidErrorAlerts = false
      if (!result) {
        this.productDetailService.submitted = false
        this.productDetailService.loading = false
        return
      }
      if (result?.id) {
        this.productDetailService.product = result
        const mdClass =
          this.productMdClassService.normalizeMdClass(template?.mdClass) ||
          this.productMdClassService.getCurrentValue(this.productDetailService.product)
        if (mdClass) {
          await this.productMdClassService.save(result, mdClass)
        }
        // todo?
        // if (this.productDetailService.product.tags?.some(tag => tag?.externalId === 'hs-count-by-piece')) {
        //   await this.createOrUpdateProductPack()
        // }
      }
      if (result?.id && mainImageToCreate) {
        await this.productCreatingService.createImage(mainImageToCreate, result.id)
      }
      if (result?.id && this.productCreatingService.childrenWithImagesToCreate?.length > 0) {
        for (const childAndImage of this.productCreatingService.childrenWithImagesToCreate) {
          childAndImage.childToCreate.parent = result
          try {
            if (this.core.services.user.user?.company?.batchExpirationMode === 'enabled-everywhere') {
              childAndImage.childToCreate.enabledBatchExpiration = true
            }
            const createdChild = await this.core.services.product.create(
              childAndImage.childToCreate,
              SchemaUtil.product,
            )
            const childMdClass =
              this.productMdClassService.getCurrentValue(childAndImage.childToCreate) ||
              this.productMdClassService.normalizeMdClass(template?.mdClass)
            if (createdChild?.id && childMdClass) {
              await this.productMdClassService.save(createdChild, childMdClass)
            }
            // todo?
            // if (this.productDetailService.product.tags?.some(tag => tag?.externalId === 'hs-count-by-piece')) {
            //   await this.createOrUpdateProductPack()
            // }
            if (childAndImage.imageToCreate)
              await this.productCreatingService.createImage(childAndImage.imageToCreate, createdChild.id)
          } catch (err) {
            console.log(err)
          }
        }
      }
      if (result?.id) {
        this.notificationService.success(this.translateService.instant('alert.product.added'))
        this.productDetailService.isCreating = false
        this.chooseProduct(result)
      }
    } catch (e) {
      console.log(e)
    } finally {
      this.notificationService.avoidErrorAlerts = false
      if (this.productDetailService.isCreating) {
        this.productDetailService.submitted = false
        this.productDetailService.loading = false
      }
    }
  }
}
